import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import stylePropType from 'react-style-proptype';
import { createPortal } from 'react-dom';
import { Transition } from 'react-transition-group';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react-dom';

const AnimatedDropdownMenu = styled.div`
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};

  border-radius: 8px;
  background-color: var(--elevation-high);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05), 0 4px 4px 0 rgba(0, 0, 0, 0.04);
  border: 1px solid var(--border-muted);

  padding: 2px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  position: absolute;

  z-index: ${({ $usePortal }) =>
    $usePortal ? 'calc(var(--z-dropdown) + 1)' : 'var(--z-dropdown)'};
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${({ $state }) => ($state === 'entering' || $state === 'entered' ? 1 : 0)};
`;

const DropdownMenu = forwardRef(
  (
    {
      children,
      isOpen = true,
      style = null,
      className = null,
      referenceElement = null,
      menuPlacement = 'bottom-start',
      menuOffset = 8,
      usePortal = false
    },
    ref
  ) => {
    const { refs, floatingStyles } = useFloating({
      elements: {
        reference: referenceElement // Use the provided reference element
      },
      placement: menuPlacement,
      middleware: [offset(menuOffset), flip(), shift()],
      whileElementsMounted: autoUpdate
    });

    // Merge the forwarded ref with Floating UI's ref
    const mergeRefs = (node) => {
      refs.setFloating(node);
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = node;
      }
    };

    const dropdownElement = (
      <Transition in={isOpen} timeout={500}>
        {(state) => (
          <AnimatedDropdownMenu
            ref={mergeRefs}
            className={className}
            $isOpen={isOpen}
            $state={state}
            $usePortal={usePortal}
            style={{ ...style, ...floatingStyles }}
          >
            {children}
          </AnimatedDropdownMenu>
        )}
      </Transition>
    );

    // Use portal for nested dropdowns to avoid scroll container interference
    if (usePortal) {
      return createPortal(dropdownElement, document.querySelector('#portal-root'));
    }

    return dropdownElement;
  }
);

DropdownMenu.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  style: stylePropType,
  className: PropTypes.string,
  referenceElement: PropTypes.instanceOf(Element),
  menuPlacement: PropTypes.string,
  menuOffset: PropTypes.number,
  usePortal: PropTypes.bool
};

export { DropdownMenu };
